import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, map, Observable, of, skip, Subscription, take } from 'rxjs';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { InvitationInterface } from '../../../models/interface/invitation.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

export type GroupMemberInvitationCheckData = {
  isManager: boolean;
  study: StudyInterface;
};

@Component({
  selector: 'app-dialog-group-member-invitation-check',
  templateUrl: './dialog-group-member-invitation-check.component.html',
  styleUrls: ['./dialog-group-member-invitation-check.component.scss'],
  providers: [StudyStore],
})
export class DialogGroupMemberInvitationCheckComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faSearch = faSearch;
  faInfoCircle = faInfoCircle;
  faEnvelope = faEnvelope;
  faCheckCircle = faCheckCircle;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faEdit = faEdit;

  public isManager: boolean;

  public study: StudyInterface;

  public pendingInvitationsData$: Observable<PaginationData<InvitationInterface>>;
  public pendingInvitations$: Observable<InvitationInterface[]>;
  public pagedInvitations$: Observable<InvitationInterface[]>;
  public pendingInvitationsDataSubject = new BehaviorSubject<PaginationData<InvitationInterface>>({ items: [] });
  public pendingInvitationsSubject = new BehaviorSubject<InvitationInterface[]>([]);
  public pagedInvitationsSubject = new BehaviorSubject<InvitationInterface[]>([]);

  public isLoading$: Observable<boolean>;

  private studyInvitationsECoach$: Observable<InvitationInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: GroupMemberInvitationCheckData,
    private studyStore: StudyStore,
    private helperService: HelperService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.pendingInvitationsData$ = this.pendingInvitationsDataSubject.asObservable();
    this.pendingInvitations$ = this.pendingInvitationsSubject.asObservable();
    this.pagedInvitations$ = this.pagedInvitationsSubject.asObservable();
    this.studyInvitationsECoach$ = this.studyStore.studyInvitationsECoach$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.isManager = this.data.isManager;
    this.study = this.data.study;

    if (this.isManager) {
      this.studyStore.getAllPendingStudyInvitations({ studyId: this.study.id });
    } else {
      this.studyStore.getEcoachStudyInvitations({ studyId: this.study.id });
    }

    this.subscriptions.push(
      this.studyInvitationsECoach$.pipe(skip(1), take(1))
        .pipe(map((pendingInvitations) => this.performPagination(pendingInvitations)))
        .subscribe({
          next: () => this.isLoadingSubject.next(false),
          error: () => this.isLoadingSubject.next(false),
        }),
    );
  }

  public removeInvitation(removedInvitation: InvitationInterface): void {
    this.isLoadingSubject.next(true);
    const pendingInvitations = this.pendingInvitationsSubject.value;
    const index = pendingInvitations.findIndex(
      (invitation) => invitation.id === removedInvitation.id,
    );
    if (index === -1) {
      this.isLoadingSubject.next(false);
    } else {
      pendingInvitations.splice(index, 1);
      this.subscriptions.push(
        this.performPagination(pendingInvitations).subscribe(
          () => this.isLoadingSubject.next(false),
        ),
      );
    }
  }

  public updateInvitation(updatedInvitation: InvitationInterface): void {
    this.isLoadingSubject.next(true);
    if (this.isManager) {
      this.studyStore.getAllPendingStudyInvitations({ studyId: this.study.id });
    } else {
      this.studyStore.getEcoachStudyInvitations({ studyId: this.study.id });
    }
    this.subscriptions.push(
      this.studyInvitationsECoach$.pipe(skip(1), take(1)).pipe(
        map((pendingInvitations: InvitationInterface[]) => {
          const notFound = pendingInvitations.every(
            (invitation) => invitation.id !== updatedInvitation.id,
          );
          if (notFound) {
            return of(null);
          } else {
            return this.performPagination(pendingInvitations);
          }
        })
      ).subscribe(() => this.isLoadingSubject.next(false)),
    );
  }

  updateStudyCode(invitation: InvitationInterface, studyCode: string | null) {
    invitation.attributes.code = studyCode;
  }

  public updatePagedInvitations(event: InvitationInterface[]) {
    if (event) {
      this.pagedInvitationsSubject.next(event);
    }
  }

  public performPagination(pendingInvitations: InvitationInterface[]) {
    this.pendingInvitationsSubject.next(pendingInvitations);
    const pageIndex = this.paginator?.paginator ? this.paginator.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator.paginator.pageSize : 20;
    return this.helper.setPagedContent(
      this.pendingInvitationsSubject,
      this.pagedInvitationsSubject,
      false,
      pageIndex,
      pageSize,
      this.pendingInvitationsDataSubject,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
